import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CustomToast from '../../components/Redesing/custom-toast';
import {
  AntChatSavedReply,
  ChatSystemOption,
  archiveSavedReply,
  createSavedReply,
  fetchChatSystems,
  fetchSavedReplies,
  updateSavedReply,
} from '../../hooks/chat/api';
import { REPLY_VARIABLES, replyScopeLabel, searchSavedReplies } from './savedReplies';

/** ant's limits on a saved reply. */
export const TITLE_MAX = 80;
export const BODY_MAX = 4000;

/** The scope picker's "every system" option. */
const GLOBAL: ChatSystemOption = { id: '', name: 'All systems (global)' };

interface Props {
  open: boolean;
  onClose: () => void;
  /** The system of the conversation on screen: the default scope of a new reply. */
  system: ChatSystemOption | null;
  /** Movatic admins may write global replies; ant refuses anyone else. */
  canManageGlobal: boolean;
  /** A reply was created, edited or archived: the composer re-reads its list. */
  onChanged: () => void;
}

type Draft = {
  /** The reply being edited; null for a new one. */
  id: string | null;
  title: string;
  body: string;
  /** A new reply's scope: GLOBAL, or a system. */
  scope: ChatSystemOption | null;
};

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? '');

/** Why a draft cannot be saved yet, or null. */
export function draftProblem(draft: Pick<Draft, 'id' | 'title' | 'body' | 'scope'>) {
  if (!draft.title.trim()) return 'Give the reply a title.';
  if (draft.title.trim().length > TITLE_MAX)
    return `The title is at most ${TITLE_MAX} characters.`;
  if (!draft.body.trim()) return 'Write the reply.';
  if (draft.body.length > BODY_MAX) return `The reply is at most ${BODY_MAX} characters.`;
  if (!draft.id && !draft.scope) return 'Pick where the reply is offered.';
  return null;
}

/**
 * Create, edit and archive saved replies. Lists every reply the agent may
 * read (global ones plus those of their systems); a new one goes to the
 * conversation's system by default, or to every system for a Movatic admin
 * who picks it. Archiving takes a reply out of the picker; ant keeps it.
 */
export function ManageSavedRepliesDialog({
  open,
  onClose,
  system,
  canManageGlobal,
  onChanged,
}: Props) {
  const [replies, setReplies] = useState<AntChatSavedReply[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  /** The reply whose Archive was clicked once: the second click archives it. */
  const [confirmArchive, setConfirmArchive] = useState<string | null>(null);
  const [systems, setSystems] = useState<ChatSystemOption[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReplies(await fetchSavedReplies());
      setLoadError(null);
    } catch (error) {
      setLoadError(errorText(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      load();
    } else {
      setDraft(null);
      setSearch('');
      setConfirmArchive(null);
    }
  }, [open, load]);

  // The systems a new reply may go to, read once, when a new one is started.
  useEffect(() => {
    if (!draft || draft.id || systems) return;
    fetchChatSystems()
      .then(setSystems)
      .catch(() => setSystems([]));
  }, [draft, systems]);

  const scopeOptions = useMemo(() => {
    const list = [...(systems ?? [])];
    if (system && !list.some((s) => s.id === system.id)) list.unshift(system);
    return canManageGlobal ? [GLOBAL, ...list] : list;
  }, [systems, system, canManageGlobal]);

  const visible = useMemo(
    () => (replies ? searchSavedReplies(replies, search) : []),
    [replies, search]
  );

  const startNew = () =>
    setDraft({
      id: null,
      title: '',
      body: '',
      scope: system ?? (canManageGlobal ? GLOBAL : null),
    });

  const canManageReply = (reply: AntChatSavedReply) =>
    Boolean(reply.systemId) || canManageGlobal;
  const canSaveDraft = draft
    ? draft.id
      ? Boolean(replies?.some((reply) => reply.id === draft.id && canManageReply(reply)))
      : Boolean(draft.scope?.id) || canManageGlobal
    : false;

  const startEdit = (reply: AntChatSavedReply) => {
    if (!canManageReply(reply)) return;
    setDraft({ id: reply.id, title: reply.title, body: reply.body, scope: null });
  };

  const save = async () => {
    if (!draft || draftProblem(draft) || !canSaveDraft) return;
    setSaving(true);
    try {
      const title = draft.title.trim();
      if (draft.id) {
        await updateSavedReply(draft.id, { title, body: draft.body });
      } else {
        await createSavedReply({
          title,
          body: draft.body,
          systemId: draft.scope?.id || null,
        });
      }
      CustomToast({ type: 'success', message: draft.id ? 'Reply saved' : 'Reply added' });
      setDraft(null);
      onChanged();
      await load();
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `Could not save the reply. ${errorText(error)}`,
        duration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  const archive = async (reply: AntChatSavedReply) => {
    if (!canManageReply(reply)) return;
    if (confirmArchive !== reply.id) {
      setConfirmArchive(reply.id);
      return;
    }
    setSaving(true);
    try {
      await archiveSavedReply(reply.id);
      CustomToast({ type: 'success', message: `"${reply.title}" archived` });
      setConfirmArchive(null);
      onChanged();
      await load();
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `Could not archive the reply. ${errorText(error)}`,
        duration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  const problem = draft ? draftProblem(draft) : null;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {draft ? (draft.id ? 'Edit reply' : 'New reply') : 'Saved replies'}
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 360 }}>
        {draft ? (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              autoFocus
              label="Title"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              inputProps={{ maxLength: TITLE_MAX, 'aria-label': 'Title' }}
              helperText={`${draft.title.length}/${TITLE_MAX}`}
              size="small"
            />
            <TextField
              label="Reply"
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              inputProps={{ maxLength: BODY_MAX, 'aria-label': 'Reply' }}
              multiline
              minRows={5}
              maxRows={14}
              helperText={`Variables: ${REPLY_VARIABLES.join(', ')} · ${
                draft.body.length
              }/${BODY_MAX}`}
            />
            {draft.id ? (
              <Typography variant="body2" color="text.secondary">
                Offered in:{' '}
                {replyScopeLabel(
                  replies?.find((r) => r.id === draft.id) ?? {
                    systemId: null,
                    system: null,
                  }
                )}
              </Typography>
            ) : (
              <Autocomplete
                options={scopeOptions}
                value={draft.scope}
                onChange={(_, value) => setDraft({ ...draft, scope: value })}
                getOptionLabel={(o) => o.name || 'Unnamed system'}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                loading={!systems}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Offered in"
                    helperText={
                      canManageGlobal
                        ? 'All systems, or only one'
                        : 'Replies for every system are for Movatic admins'
                    }
                  />
                )}
              />
            )}
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Stack direction="row" gap={1}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search saved replies"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                inputProps={{ 'aria-label': 'Search saved replies' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={startNew}
                sx={{ flexShrink: 0 }}
              >
                New reply
              </Button>
            </Stack>
            {loadError && (
              <Alert severity="error">Could not load replies. {loadError}</Alert>
            )}
            {loading && !replies ? (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress size={22} />
              </Stack>
            ) : visible.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3 }}
                align="center"
              >
                {replies?.length ? 'No reply matches' : 'No saved replies yet'}
              </Typography>
            ) : (
              <List dense disablePadding>
                {visible.map((reply) => (
                  <ListItem
                    key={reply.id}
                    divider
                    disableGutters
                    sx={{ alignItems: 'flex-start', gap: 1 }}
                    data-testid="manage-reply-row"
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {reply.title}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={replyScopeLabel(reply)}
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {reply.body}
                      </Typography>
                    </Box>
                    <Stack direction="row" gap={0.5} sx={{ flexShrink: 0 }}>
                      <Button
                        size="small"
                        sx={{ float: 'none' }}
                        disabled={saving || !canManageReply(reply)}
                        onClick={() => startEdit(reply)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        sx={{ float: 'none' }}
                        disabled={saving || !canManageReply(reply)}
                        onClick={() => archive(reply)}
                        onBlur={() =>
                          setConfirmArchive((id) => (id === reply.id ? null : id))
                        }
                      >
                        {confirmArchive === reply.id ? 'Confirm archive' : 'Archive'}
                      </Button>
                    </Stack>
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {draft ? (
          <>
            <Button onClick={() => setDraft(null)} disabled={saving}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={save}
              disabled={saving || Boolean(problem) || !canSaveDraft}
              startIcon={
                saving ? <CircularProgress size={14} color="inherit" /> : undefined
              }
            >
              {draft.id ? 'Save' : 'Add reply'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} disabled={saving}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
